import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

export default function BookingConfirmedPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('verifying'); // verifying | paid | failed

  useEffect(() => {
    if (!code || !sessionId) {
      setStatus('failed');
      return;
    }
    // Verify payment with Stripe and update booking status
    api.post('/v2/public/bookings/verify-payment', {
      confirmationCode: code,
      sessionId,
    })
      .then(res => {
        if (res.data.paymentStatus === 'paid' || res.data.alreadyPaid) {
          setStatus('paid');
          // Fire B2C conversion — customer just completed a marketplace booking
          import('../utils/analytics').then(({ trackMarketplaceBooking, initMetaPixel }) => {
            initMetaPixel();
            trackMarketplaceBooking({
              confirmationCode: code,
              serviceId:   res.data.serviceId   || '',
              serviceName: res.data.serviceName || 'Booking',
              totalPrice:  res.data.totalPrice  || res.data.paidAmount || 0,
              currency:    res.data.currency    || 'USD',
              orgName:     res.data.orgName     || '',
              partySize:   res.data.partySize   || 1,
            });
          });
        } else {
          setStatus('failed');
        }
      })
      .catch(() => setStatus('failed'));
  }, [code, sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Confirming payment...</h1>
            <p className="text-gray-500 text-sm">Hang tight, we're verifying with Stripe.</p>
          </>
        )}

        {status === 'paid' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment confirmed!</h1>
            <p className="text-gray-500 text-sm mb-2">Your booking is confirmed.</p>
            {code && (
              <p className="font-mono text-sm font-semibold text-gray-700 mb-8">{code}</p>
            )}
            <button
              onClick={() => navigate(code ? `/customer-ticket/${code}` : '/')}
              className="w-full h-12 text-base font-semibold rounded-lg text-white"
              style={{ backgroundColor: '#2563EB' }}
            >
              View Your Ticket
            </button>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Couldn't confirm payment</h1>
            <p className="text-gray-500 text-sm mb-8">
              Your payment may have gone through — check your email for a confirmation, or contact support with your booking code.
            </p>
            {code && (
              <button
                onClick={() => navigate(`/customer-ticket/${code}`)}
                className="w-full h-12 text-base font-semibold rounded-lg text-white mb-3"
                style={{ backgroundColor: '#2563EB' }}
              >
                View Your Ticket
              </button>
            )}
            <button
              onClick={() => navigate('/')}
              className="w-full h-12 text-base font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              Back to Home
            </button>
          </>
        )}
      </div>
    </div>
  );
}
